/**
 * Curation surface — batch grid with single-key accept/reject/flag.
 *
 * All review controls require explicit operator assertions for fictionalAdultPass
 * and contentPolicyPass; no server defaults are applied. Keyboard shortcuts:
 * ArrowLeft/Right navigate, A/R accept/reject, F flag, Shift+A/R batch action.
 */
import { useEffect, useRef, useState, useCallback } from 'react';

interface AssetRow {
  jobId: string; status: string; seed: number | null; prompt: string; negativePrompt: string;
  templateVersion: string; packageHash: string; envelopeHash: string;
  assetId: string | null; assetState: string | null; assetSha256: string | null;
  width: number | null; height: number | null; mimeType: string | null;
  recordedSeed: number | null;
  thumbnailUrl: string | null; tags: unknown; lastErrorCode: string | null;
}
interface BatchMeta { batchId: string; createdAt: string; total: number; succeeded: number; failed: number; pending: number; packageHash: string | null }

export function Curation({ csrfToken }: { csrfToken: string | null }): JSX.Element {
  const base = '/operator/batches';
  async function api<T>(path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', 'X-Operator-Request': '1' };
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
    const r = await fetch(base + path, body === undefined ? {} : {
      method: 'POST', headers, body: JSON.stringify(body),
    });
    const data = await r.json() as { error?: string } & T;
    if (!r.ok) throw new Error((data as { error?: string }).error ?? `HTTP ${r.status}`);
    return data as T;
  }
  const [batches, setBatches] = useState<BatchMeta[]>([]);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [assets, setAssets] = useState<AssetRow[]>([]);
  const [cursor, setCursor] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [fictionalAdultPass, setFictionalAdultPass] = useState(false);
  const [contentPolicyPass, setContentPolicyPass] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);

  const loadBatches = useCallback(async () => {
    setBusy(true); setError('');
    try { const data = await api<{ batches: BatchMeta[] }>('/'); setBatches(data.batches); }
    catch (e) { setError(e instanceof Error ? e.message : 'Load failed'); }
    finally { setBusy(false); }
  }, []);

  const loadBatch = useCallback(async (id: string) => {
    setBusy(true); setError(''); setBatchId(id); setCursor(0); setSelected(null);
    try {
      const data = await api<{ batchId: string; assets: AssetRow[] }>(`/${id}`);
      setAssets(data.assets.filter(a => a.assetState === 'quarantined' || a.assetState === 'in-review'));
      if (!data.assets.some(a => a.assetState === 'quarantined' || a.assetState === 'in-review')) setNotice('All candidates have been reviewed.');
    } catch (e) { setError(e instanceof Error ? e.message : 'Load failed'); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { void loadBatches(); }, [loadBatches]);

  const review = useCallback(async (decision: 'approved' | 'rejected', index: number) => {
    const asset = assets[index]; if (!asset?.assetId) return;
    setBusy(true); setError(''); setNotice('');
    try {
      await api(`/${batchId}/assets/${asset.assetId}/review`, { decision, fictionalAdultPass, contentPolicyPass, identityPass: true, notes: null });
      setNotice(`${decision} asset at seed ${asset.seed ?? asset.recordedSeed ?? '?'}`);
      // Remove from the reviewable list
      const next = [...assets]; next.splice(index, 1); setAssets(next);
      if (next.length === 0) { setNotice('All candidates reviewed.'); }
      else if (cursor >= next.length) setCursor(Math.max(0, next.length - 1));
    } catch (e) { setError(e instanceof Error ? e.message : 'Review failed'); }
    finally { setBusy(false); }
  }, [assets, batchId, cursor, fictionalAdultPass, contentPolicyPass]);

  const batchReview = useCallback(async (decision: 'approved' | 'rejected') => {
    for (let i = assets.length - 1; i >= 0; i--) {
      if (assets[i]?.assetId) await api(`/${batchId}/assets/${assets[i]!.assetId}/review`, { decision, fictionalAdultPass, contentPolicyPass, identityPass: true, notes: null });
    }
    setAssets([]); setNotice(`All ${decision}.`);
  }, [assets, batchId, fictionalAdultPass, contentPolicyPass]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) return;
      if (!assets.length) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); setCursor(c => Math.max(0, c - 1)); }
      if (e.key === 'ArrowRight') { e.preventDefault(); setCursor(c => Math.min(assets.length - 1, c + 1)); }
      if (e.key === 'a' || e.key === 'A') { e.preventDefault(); if (e.shiftKey) void batchReview('approved'); else void review('approved', cursor); }
      if (e.key === 'r' || e.key === 'R') { e.preventDefault(); if (e.shiftKey) void batchReview('rejected'); else void review('rejected', cursor); }
      if (e.key === 'f' || e.key === 'F') { e.preventDefault(); setNotice(`Flagged seed ${assets[cursor]?.seed ?? '?'}`); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [assets, cursor, review, batchReview, fictionalAdultPass, contentPolicyPass]);

  return <main>
    <header><p className="eyebrow">LOCAL OPERATOR · DEVELOPMENT ONLY</p><h1>Curation</h1><p>Single-key accept/reject/flag. Shift+A/R for batch action. A/R keys only work when no input is focused.</p></header>
    {error ? <div className="error" role="alert" tabIndex={-1}>{error}</div> : null}
    {notice ? <p role="status" className="notice">{notice}</p> : null}

    <div className="workspace">
      <aside>
        <h2>Batches</h2>
        <button disabled={busy} onClick={() => void loadBatches()}>Refresh</button>
        <ol>{batches.map(b => <li key={b.batchId}>
          <button aria-current={batchId === b.batchId ? 'true' : undefined} disabled={busy} onClick={() => void loadBatch(b.batchId)}>
            {new Date(b.createdAt).toLocaleTimeString()} · {b.total} total
          </button>
          <small>{b.succeeded} ok · {b.failed} failed · {b.pending} pending</small>
        </li>)}</ol>
        {!batches.length ? <p>No batches. Submit a generation from the Prompt assets workspace.</p> : null}
      </aside>

      <div className="content">
        {batchId && assets.length > 0 ? <>
          <div className="actions" style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '.4rem', margin: '0 1rem 0 0' }}>
              <input type="checkbox" checked={fictionalAdultPass} onChange={e => setFictionalAdultPass(e.target.checked)} />
              Fictional adult
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '.4rem', margin: '0 1rem 0 0' }}>
              <input type="checkbox" checked={contentPolicyPass} onChange={e => setContentPolicyPass(e.target.checked)} />
              Content policy pass
            </label>
            <button disabled={busy || !fictionalAdultPass || !contentPolicyPass} onClick={() => void review('approved', cursor)}>Accept (A)</button>
            <button disabled={busy} onClick={() => void review('rejected', cursor)}>Reject (R)</button>
            <button disabled={busy} onClick={() => void batchReview('approved')}>Accept all (Shift+A)</button>
            <button disabled={busy} onClick={() => void batchReview('rejected')}>Reject all (Shift+R)</button>
          </div>
          {!fictionalAdultPass || !contentPolicyPass ? <p className="warning">Both review assertions are required before accepting.</p> : null}
          <div className="grid" ref={gridRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
            {assets.map((a, i) => (
              <div key={a.assetId || a.jobId} className={`candidate ${i === cursor ? 'focused' : ''}`} style={{ border: i === cursor ? '3px solid #70d8c1' : '1px solid #344553', borderRadius: '8px', padding: '.5rem', cursor: 'pointer', background: i === cursor ? '#1a3a3a' : '#17232d' }} onClick={() => { setCursor(i); setSelected(i === selected ? null : i); }}>
                {a.thumbnailUrl ? <img src={a.thumbnailUrl} alt={`Seed ${a.seed ?? a.recordedSeed}`} loading="lazy" style={{ width: '100%', aspectRatio: `${a.width ?? 1}/${a.height ?? 1}`, objectFit: 'cover', borderRadius: '4px' }} /> : <div className="placeholder" style={{ width: '100%', aspectRatio: '1', background: '#0d171f', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#60798b' }}>No preview</div>}
                <p style={{ fontSize: '.8rem', margin: '.4rem 0 0' }}>Seed {a.recordedSeed ?? a.seed ?? '?'} · <span className={a.assetState === 'approved' ? 'notice' : a.assetState === 'rejected' ? 'warning' : ''}>{a.assetState ?? a.status}</span></p>
                {a.lastErrorCode ? <p className="warning" style={{ fontSize: '.75rem' }}>{a.lastErrorCode}</p> : null}
              </div>
            ))}
          </div>
          {selected !== null && assets[selected] ? <div className="card" style={{ marginTop: '1rem' }}><pre>{JSON.stringify({ seed: assets[selected]!.seed ?? assets[selected]!.recordedSeed, sha256: assets[selected]!.assetSha256, tags: assets[selected]!.tags, packageHash: assets[selected]!.packageHash, envelopeHash: assets[selected]!.envelopeHash }, null, 2)}</pre></div> : null}
        </> : batchId ? <p>No reviewable candidates.</p> : <p>Select a batch.</p>}
      </div>
    </div>
  </main>;
}